/**
 * WabaDirectoryBootstrapPanel — P0-6 FASE 2E-UI + 2E-APPLY.
 *
 * Endpoint utilizzati:
 *   GET  /api/admin/messaging/waba-directory/bootstrap-dry-run  (read-only)
 *   POST /api/admin/messaging/waba-directory/bootstrap-apply    (super_admin)
 *
 * Il pulsante "Importa nel CRM" appare SOLO se il dry-run ha
 * `safe_to_apply=true` (COLLISION=0, ERROR=0). Al click apre modale di
 * conferma esplicita; solo il pulsante "Conferma importazione" invoca
 * POST. Double-click prevenuto tramite `applying` state. Dopo il POST
 * viene mostrato il report reale e rilanciato automaticamente il
 * dry-run per aggiornare i contatori.
 *
 * PII-safe: nessun nome/telefono/user_id renderizzati.
 */
import React, { useState, useCallback, useEffect, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  sha256File,
  loadFromStorage,
  saveToStorage,
  resetStorage,
  validateBackupShape,
  backupMatchesCurrentVcf,
  extractVcfMeta,
} from "@/lib/vcfReviewPersistence";

const DIRECTORY_LABELS = [
  ["total", "Contatti totali"],
  ["active", "Attivi"],
  ["inactive_or_removed", "Rimossi/inattivi"],
  ["with_phone", "Con telefono"],
  ["phone_valid_normalizable", "Telefono valido"],
  ["no_phone", "Senza telefono"],
  ["phone_invalid", "Telefono non valido"],
  ["already_linked_customer_id", "Già collegati al CRM"],
  ["without_customer_id", "Non ancora collegati"],
];

const CLASSIFICATION_LABELS = [
  ["WOULD_LINK_EXISTING", "Clienti CRM già esistenti da collegare"],
  ["WOULD_CREATE", "Nuovi clienti da creare"],
  ["WOULD_UPDATE_NAME", "Clienti con nome/cognome da aggiornare"],
  ["WOULD_NOOP", "Nessuna modifica necessaria"],
  ["SKIP_INACTIVE", "Contatti inattivi ignorati"],
  ["SKIP_NO_PHONE", "Contatti senza telefono ignorati"],
  ["SKIP_INVALID_PHONE", "Telefoni non validi ignorati"],
  ["COLLISION", "Collisioni"],
  ["ERROR", "Errori"],
];

function StatRow({ label, value, danger = false, testId }) {
  return (
    <div
      className="flex justify-between items-center px-3 py-2 text-sm border-b border-ink-100/60 last:border-b-0"
      data-testid={testId}
    >
      <span className="text-ink-600">{label}</span>
      <span
        className={
          "font-mono tabular-nums font-semibold " +
          (danger && Number(value) > 0
            ? "text-red-700"
            : "text-ink-900")
        }
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {value ?? 0}
      </span>
    </div>
  );
}


function fmtRome(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" });
}

const INCREMENTAL_VIEW = {
  active_verified: { cls: "border-emerald-200 bg-emerald-50 text-emerald-900", label: "Attivi (verificato)" },
  no_recent_events: { cls: "border-amber-200 bg-amber-50 text-amber-900", label: "Nessun evento recente" },
  no_events_in_retention: { cls: "border-ink-200 bg-ink-50 text-ink-800", label: "Nessun evento registrato" },
  paused_historical: { cls: "border-blue-200 bg-blue-50 text-blue-900", label: "In pausa (sessione storica in corso)" },
  unknown: { cls: "border-ink-200 bg-ink-50 text-ink-800", label: "Stato non determinabile" },
};

function IncrementalSyncStatus({ inc }) {
  const st = inc?.status || "unknown";
  const v = INCREMENTAL_VIEW[st] || INCREMENTAL_VIEW.unknown;
  const days = inc?.recent_window_days ?? 7;
  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${v.cls}`} data-testid="waba-incremental-status">
      <div className="font-semibold" data-testid="waba-incremental-status-label">
        Aggiornamenti incrementali (webhook): {v.label}
      </div>
      <div className="text-xs mt-1 space-y-0.5">
        <div data-testid="waba-incremental-last-event">
          Ultimo evento rubrica ricevuto: {inc?.last_event_at ? fmtRome(inc.last_event_at) : "nessuno"}
          {inc?.events_recent != null ? ` · ${inc.events_recent} ${inc.events_recent === 1 ? "evento" : "eventi"} negli ultimi ${days} giorni` : ""}
        </div>
        {inc?.directory_last_updated_at && (
          <div data-testid="waba-incremental-dir-updated">
            Ultimo aggiornamento directory: {fmtRome(inc.directory_last_updated_at)}
          </div>
        )}
        {st === "no_events_in_retention" && (
          <div>Gli eventi webhook sono conservati {inc?.retention_days ?? 30} giorni: l'assenza non prova un guasto.</div>
        )}
      </div>
    </div>
  );
}

function friendlyError(err) {
  const status = err?.response?.status;
  if (status === 401) {
    return "Sessione scaduta o non autenticata. Ricarica la pagina ed effettua di nuovo l'accesso.";
  }
  if (status === 403) {
    return "Solo un Super Admin può eseguire questa analisi.";
  }
  if (status && status >= 500) {
    return "Errore del server durante l'analisi. Riprova tra qualche minuto o contatta il supporto.";
  }
  return formatApiError(err) || "Errore imprevisto durante l'analisi.";
}

// P0-6 FASE 2F.1C · Etichette italiane per gli enum backend.
// Gli enum tecnici restano interni al DB/API; l'utente vede solo IT.
const OPTION_LABEL_IT = {
  SAME_PERSON_MERGE: "Stessa persona · Unisci duplicati",
  KEEP_VCARD_A: "Mantieni il primo contatto",
  KEEP_VCARD_B: "Mantieni il secondo contatto",
  SKIP_ALL: "Non importare questi contatti",
  MANUAL_REVIEW: "Da verificare manualmente",
  USE_VCF_NAME: "Usa nome della rubrica iPhone",
  KEEP_CRM_NAME: "Mantieni nome attuale CRM",
  SUGGESTED_SPLIT: "Separazione suggerita",
  SUGGESTED_SPLIT_COMPOUND: "Separazione composta suggerita",
  KEEP_NAME_ONLY: "Mantieni solo Nome",
};

const CASE_CLASS_LABEL_IT = {
  SUGGESTED_SPLIT: "Separazione suggerita",
  SUGGESTED_SPLIT_COMPOUND: "Separazione composta suggerita",
  MANUAL_REVIEW: "Da verificare manualmente",
  KEEP_NAME_ONLY: "Mantieni solo Nome",
  // P0-6 FASE 2F.1F · sub-classificazione dei singoli-token
  PROBABLE_GIVEN_NAME: "Probabile nome",
  PROBABLE_SURNAME: "Probabile cognome",
  SINGLE_NAME_AMBIGUOUS: "Da verificare",
};

const CASE_CLASS_BADGE_CLS = {
  SUGGESTED_SPLIT: "bg-emerald-100 text-emerald-800 border-emerald-200",
  SUGGESTED_SPLIT_COMPOUND:
    "bg-teal-100 text-teal-900 border-teal-200",
  MANUAL_REVIEW: "bg-amber-100 text-amber-900 border-amber-200",
  KEEP_NAME_ONLY: "bg-blue-100 text-blue-900 border-blue-200",
  PROBABLE_GIVEN_NAME: "bg-sky-100 text-sky-900 border-sky-200",
  PROBABLE_SURNAME: "bg-indigo-100 text-indigo-900 border-indigo-200",
  SINGLE_NAME_AMBIGUOUS: "bg-yellow-100 text-yellow-900 border-yellow-200",
};

const MANUAL_REASON_LABEL_IT = {
  parentesi: "Parentesi/annotazione",
  slash: "Slash /",
  numeri: "Contiene numeri",
  descrizione_trattino: "Descrizione con trattino",
  punteggiatura: "Punteggiatura ambigua",
  struttura_nome_ambigua: "Struttura del nome ambigua",
};

// Reviewed-status locale (stato solo React, mai persistito).
// - undefined: da revisionare
// - "confirmed": admin ha confermato (con eventuali proposed_name/surname
//   personalizzati)
// - "kept_name_only": admin ha scelto di mantenere solo il nome
// - "skipped": admin ha scelto di non importare

// P0-6 FASE 2F.1C · Revisione interattiva "Contatti senza cognome".
// Stato SOLO locale (React state). Nessuna persistenza, nessun POST/PATCH.
function SurnameMissingReview({ items, audit, decisions, setDecisions,
                                  drafts, setDrafts }) {
  const [filter, setFilter] = React.useState("all");
  const [showBulkConfirm, setShowBulkConfirm] = React.useState(false);
  const [showBulkProbableGiven, setShowBulkProbableGiven] =
    React.useState(false);
  const [bulkResult, setBulkResult] = React.useState(null);

  const totalMissing = audit.SURNAME_MISSING || items.length;
  const totalSuggested =
    audit.SURNAME_MISSING_WITH_MULTIWORD_GIVEN_NAME || 0;
  const totalKeepOnly =
    audit.SURNAME_MISSING_WITH_SINGLEWORD_GIVEN_NAME || 0;
  const totalReviewed = Object.keys(decisions).length;
  const totalToReview = totalMissing - totalReviewed;

  // Contatori per filtro
  const perClass = items.reduce((acc, it) => {
    acc[it.case_class || "MANUAL_REVIEW"] =
      (acc[it.case_class || "MANUAL_REVIEW"] || 0) + 1;
    return acc;
  }, {});
  const suggestedCount = perClass.SUGGESTED_SPLIT || 0;
  const compoundCount = perClass.SUGGESTED_SPLIT_COMPOUND || 0;
  const manualCount = perClass.MANUAL_REVIEW || 0;
  const keepOnlyCount = perClass.KEEP_NAME_ONLY || 0;
  const probableGivenCount = perClass.PROBABLE_GIVEN_NAME || 0;
  const probableSurnameCount = perClass.PROBABLE_SURNAME || 0;
  const ambiguousCount = perClass.SINGLE_NAME_AMBIGUOUS || 0;

  const filtered = items.filter((s) => {
    const reviewed = !!decisions[s.vcard_idx];
    if (filter === "reviewed") return reviewed;
    if (filter === "to_review") return !reviewed;
    if (filter === "suggested") return s.case_class === "SUGGESTED_SPLIT";
    if (filter === "compound")
      return s.case_class === "SUGGESTED_SPLIT_COMPOUND";
    if (filter === "manual") return s.case_class === "MANUAL_REVIEW";
    if (filter === "keep_only") return s.case_class === "KEEP_NAME_ONLY";
    if (filter === "probable_given")
      return s.case_class === "PROBABLE_GIVEN_NAME";
    if (filter === "probable_surname")
      return s.case_class === "PROBABLE_SURNAME";
    if (filter === "ambiguous")
      return s.case_class === "SINGLE_NAME_AMBIGUOUS";
    return true;
  });

  const draftFor = (it) => {
    const d = drafts[it.vcard_idx];
    const dec = decisions[it.vcard_idx];
    return {
      name: d?.name ?? dec?.proposed_name ?? it.proposed_name ?? "",
      surname: d?.surname ?? dec?.proposed_surname ?? it.proposed_surname ?? "",
      notes: d?.notes ?? dec?.proposed_notes ?? it.proposed_notes ?? "",
    };
  };
  const setDraft = (idx, name, surname, notes) =>
    setDrafts({ ...drafts, [idx]: { name, surname, notes } });

  const confirmRow = (it) => {
    const d = draftFor(it);
    setDecisions({
      ...decisions,
      [it.vcard_idx]: {
        status: "confirmed",
        proposed_name: d.name.trim(),
        proposed_surname: d.surname.trim(),
        proposed_notes: d.notes.trim(),
      },
    });
  };
  const keepNameOnly = (it) => {
    const d = draftFor(it);
    setDecisions({
      ...decisions,
      [it.vcard_idx]: {
        status: "kept_name_only",
        proposed_name: d.name.trim() || it.proposed_name || "",
        proposed_surname: "",
        proposed_notes: d.notes.trim(),
      },
    });
    setDraft(it.vcard_idx, d.name, "", d.notes);
  };
  const skipRow = (it) => {
    setDecisions({
      ...decisions,
      [it.vcard_idx]: { status: "skipped" },
    });
  };
  const undoDecision = (it) => {
    const { [it.vcard_idx]: _, ...rest } = decisions;
    setDecisions(rest);
  };

  const doBulkConfirmSuggested = () => {
    const next = { ...decisions };
    for (const it of items) {
      // FASE 2F.1D · l'azione massiva "sicura" copre SUGGESTED_SPLIT e
      // SUGGESTED_SPLIT_COMPOUND. MANUAL_REVIEW / KEEP_NAME_ONLY esclusi.
      if (it.case_class !== "SUGGESTED_SPLIT"
          && it.case_class !== "SUGGESTED_SPLIT_COMPOUND") continue;
      if (next[it.vcard_idx]) continue;
      next[it.vcard_idx] = {
        status: "confirmed",
        proposed_name: (drafts[it.vcard_idx]?.name ?? it.proposed_name
          ?? "").trim(),
        proposed_surname: (drafts[it.vcard_idx]?.surname
          ?? it.proposed_surname ?? "").trim(),
        proposed_notes: (drafts[it.vcard_idx]?.notes
          ?? it.proposed_notes ?? "").trim(),
      };
    }
    setDecisions(next);
    setShowBulkConfirm(false);
  };

  const bulkPending = items.filter(
    (it) => (it.case_class === "SUGGESTED_SPLIT"
              || it.case_class === "SUGGESTED_SPLIT_COMPOUND")
              && !decisions[it.vcard_idx]
  ).length;

  // P0-6 FASE 2F.1I · Bulk-confirm ristretto: SOLO PROBABLE_GIVEN_NAME
  // non revisionati e senza draft attivo. Non tocca collisioni/conflitti.
  const bulkProbableGivenPending = items.filter(
    (it) => it.case_class === "PROBABLE_GIVEN_NAME"
              && !decisions[it.vcard_idx]
              && !drafts[it.vcard_idx]
  );
  const bulkProbableGivenCount = bulkProbableGivenPending.length;
  const bulkProbableGivenSkippedDraft = items.filter(
    (it) => it.case_class === "PROBABLE_GIVEN_NAME"
              && !decisions[it.vcard_idx]
              && drafts[it.vcard_idx]
  ).length;

  const doBulkConfirmProbableGiven = () => {
    // Idempotente: `next[idx]` esistente → skip. Draft attivo → skip.
    // Per ogni record eleggibile: crea decisione "confirmed" con
    // proposed_name dal classifier (allowlist), surname vuoto, notes
    // preservate se già presenti nel classifier output.
    const next = { ...decisions };
    let confirmed = 0;
    let skippedReviewed = 0;
    let skippedDraft = 0;
    for (const it of items) {
      if (it.case_class !== "PROBABLE_GIVEN_NAME") continue;
      if (next[it.vcard_idx]) { skippedReviewed++; continue; }
      if (drafts[it.vcard_idx]) { skippedDraft++; continue; }
      next[it.vcard_idx] = {
        status: "confirmed",
        proposed_name: (it.proposed_name || "").trim(),
        proposed_surname: "",
        proposed_notes: (it.proposed_notes || "").trim(),
      };
      confirmed++;
    }
    setDecisions(next);
    setShowBulkProbableGiven(false);
    setBulkResult({
      kind: "probable_given",
      confirmed,
      skipped_reviewed: skippedReviewed,
      skipped_draft: skippedDraft,
    });
  };

  const FilterBtn = ({ id, label, count, testId }) => (
    <button
      type="button"
      onClick={() => setFilter(id)}
      className={
        "px-2 py-1 rounded-full font-medium text-[11px] " +
        (filter === id
          ? "bg-orange-600 text-white"
          : "bg-white text-orange-800 border border-orange-300")
      }
      data-testid={testId}
    >
      {label} ({count})
    </button>
  );

  return (
    <div
      className="rounded-lg border border-orange-200 bg-orange-50 overflow-hidden"
      data-testid="waba-vcf-review-surname"
    >
      <div className="bg-orange-100 px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-semibold uppercase tracking-wide text-orange-900">
          Contatti senza cognome · {totalMissing}
        </div>
        <div
          className="text-[11px] text-orange-900"
          data-testid="waba-vcf-surname-counters"
        >
          Revisionati: <strong>{totalReviewed}</strong> / {totalMissing} ·
          Da revisionare: <strong>{totalToReview}</strong>
        </div>
      </div>
      <div className="px-3 py-2 flex items-center gap-1 flex-wrap border-b border-orange-100">
        <FilterBtn id="all" label="Tutti" count={totalMissing}
          testId="waba-vcf-surname-filter-all" />
        <FilterBtn id="suggested" label="Separazione suggerita"
          count={suggestedCount}
          testId="waba-vcf-surname-filter-suggested" />
        <FilterBtn id="compound" label="Separazione composta suggerita"
          count={compoundCount}
          testId="waba-vcf-surname-filter-compound" />
        <FilterBtn id="manual" label="Da verificare manualmente"
          count={manualCount}
          testId="waba-vcf-surname-filter-manual" />
        <FilterBtn id="probable_given" label="Probabile nome"
          count={probableGivenCount}
          testId="waba-vcf-surname-filter-probable-given" />
        <FilterBtn id="probable_surname" label="Probabile cognome"
          count={probableSurnameCount}
          testId="waba-vcf-surname-filter-probable-surname" />
        <FilterBtn id="ambiguous" label="Da verificare"
          count={ambiguousCount}
          testId="waba-vcf-surname-filter-ambiguous" />
        {keepOnlyCount > 0 && (
          <FilterBtn id="keep_only" label="Solo Nome"
            count={keepOnlyCount}
            testId="waba-vcf-surname-filter-keep" />
        )}
        <FilterBtn id="reviewed" label="Già revisionati"
          count={totalReviewed}
          testId="waba-vcf-surname-filter-reviewed" />
        <FilterBtn id="to_review" label="Da revisionare"
          count={totalToReview}
          testId="waba-vcf-surname-filter-toreview" />
        {bulkPending > 0 && (
          <button
            type="button"
            onClick={() => setShowBulkConfirm(true)}
            className="ml-auto px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="waba-vcf-surname-bulk-btn"
          >
            Conferma tutti i suggerimenti sicuri ({bulkPending})
          </button>
        )}
        {bulkProbableGivenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowBulkProbableGiven(true)}
            className={
              (bulkPending > 0 ? "" : "ml-auto ") +
              "px-3 py-1 rounded-full text-[11px] font-semibold bg-sky-600 text-white hover:bg-sky-700"
            }
            data-testid="waba-vcf-surname-bulk-probable-given-btn"
          >
            Conferma Probabile nome ({bulkProbableGivenCount})
          </button>
        )}
      </div>

      {bulkResult && bulkResult.kind === "probable_given" && (
        <div
          className="mx-3 mt-2 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs text-sky-900 flex items-center justify-between"
          data-testid="waba-vcf-bulk-probable-given-result"
        >
          <div>
            ✅ Bulk "Probabile nome" completato ·
            {" "}<strong>{bulkResult.confirmed}</strong> confermati ·
            {" "}{bulkResult.skipped_reviewed} già revisionati (invariati) ·
            {" "}{bulkResult.skipped_draft} con bozza attiva (invariati)
          </div>
          <button
            type="button"
            onClick={() => setBulkResult(null)}
            className="text-sky-700 hover:text-sky-900 text-[10px] underline"
            data-testid="waba-vcf-bulk-result-dismiss"
          >
            chiudi
          </button>
        </div>
      )}

      <div className="p-3 max-h-[540px] overflow-auto">
        <table className="min-w-full text-xs">
          <thead className="text-left text-ink-600 sticky top-0 bg-orange-50">
            <tr>
              <th className="px-2 py-1">Rubrica iPhone</th>
              <th className="px-2 py-1">Classificazione</th>
              <th className="px-2 py-1">Nome proposto</th>
              <th className="px-2 py-1">Cognome proposto</th>
              <th className="px-2 py-1">Note proposte</th>
              <th className="px-2 py-1">Telefono</th>
              <th className="px-2 py-1">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="waba-vcf-surname-list">
            {filtered.map((s, si) => {
              const dec = decisions[s.vcard_idx];
              const draft = draftFor(s);
              const primaryTel = s.tels?.find((t) => t.valid)
                || s.tels?.[0];
              const rowClass = dec
                ? "border-t border-orange-100 opacity-70"
                : "border-t border-orange-100";
              return (
                <tr
                  key={s.vcard_idx}
                  className={rowClass}
                  data-testid={`waba-vcf-surname-row-${si}`}
                >
                  <td className="px-2 py-1 align-top">
                    <div className="text-ink-900">
                      Nome: <strong>{s.n_given || "—"}</strong>
                    </div>
                    <div className="text-ink-600">
                      Cognome: {s.n_family || "—"}
                    </div>
                    {s.has_decorative_emoji && (
                      <span className="text-[10px] px-1 rounded bg-pink-100 text-pink-800 border border-pink-200">
                        emoji nel nome
                      </span>
                    )}
                    {s.crm_matches?.length > 0 && (
                      <div className="text-[10px] text-blue-800">
                        Cliente CRM presente
                      </div>
                    )}
                    {s.waba_present && (
                      <div className="text-[10px] text-emerald-800">
                        Presente in WhatsApp
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1 align-top">
                    <span
                      className={
                        "text-[10px] px-1.5 py-0.5 rounded-full border font-medium "
                        + (CASE_CLASS_BADGE_CLS[s.case_class]
                          || "bg-ink-50 text-ink-700 border-ink-200")
                      }
                      data-testid={`waba-vcf-surname-row-${si}-class`}
                    >
                      {CASE_CLASS_LABEL_IT[s.case_class] || "Da valutare"}
                    </span>
                  </td>
                  <td className="px-2 py-1 align-top">
                    <input
                      type="text"
                      className="text-xs border border-orange-200 rounded px-2 py-1 w-32"
                      value={draft.name}
                      onChange={(e) => setDraft(
                        s.vcard_idx, e.target.value, draft.surname,
                        draft.notes)}
                      placeholder="Nome"
                      data-testid={`waba-vcf-surname-row-${si}-input-name`}
                    />
                  </td>
                  <td className="px-2 py-1 align-top">
                    <input
                      type="text"
                      className="text-xs border border-orange-200 rounded px-2 py-1 w-32"
                      value={draft.surname}
                      onChange={(e) => setDraft(
                        s.vcard_idx, draft.name, e.target.value,
                        draft.notes)}
                      placeholder="Cognome (facoltativo)"
                      data-testid={`waba-vcf-surname-row-${si}-input-surname`}
                    />
                  </td>
                  <td className="px-2 py-1 align-top">
                    <input
                      type="text"
                      className="text-xs border border-orange-200 rounded px-2 py-1 w-40"
                      value={draft.notes}
                      onChange={(e) => setDraft(
                        s.vcard_idx, draft.name, draft.surname,
                        e.target.value)}
                      placeholder="Note (facoltative)"
                      data-testid={`waba-vcf-surname-row-${si}-input-notes`}
                    />
                    {s.annotation_extracted && (
                      <div className="mt-1">
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-900 border border-purple-200 font-medium"
                          data-testid={`waba-vcf-surname-row-${si}-annotation-badge`}
                        >
                          Annotazione rilevata
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1 font-mono text-ink-600 align-top">
                    {primaryTel?.normalized || primaryTel?.raw || "—"}
                  </td>
                  <td className="px-2 py-1 align-top">
                    {dec ? (
                      <div className="flex flex-col items-start gap-1">
                        <span className="text-[10px] text-emerald-800 font-semibold">
                          {dec.status === "confirmed"
                            ? "Confermato"
                            : dec.status === "kept_name_only"
                              ? "Solo Nome"
                              : "Non importato"}
                        </span>
                        <button
                          type="button"
                          onClick={() => undoDecision(s)}
                          className="text-[10px] text-ink-500 underline"
                          data-testid={`waba-vcf-surname-row-${si}-undo`}
                        >
                          Annulla decisione
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(s.case_class === "SUGGESTED_SPLIT"
                          || s.case_class === "SUGGESTED_SPLIT_COMPOUND") ? (
                          <button
                            type="button"
                            onClick={() => confirmRow(s)}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                            data-testid={`waba-vcf-surname-row-${si}-confirm-suggested`}
                          >
                            Conferma suggerimento
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => confirmRow(s)}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                            data-testid={`waba-vcf-surname-row-${si}-confirm`}
                          >
                            Conferma
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => keepNameOnly(s)}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-blue-600 text-white hover:bg-blue-700"
                          data-testid={`waba-vcf-surname-row-${si}-keep`}
                        >
                          Mantieni solo Nome
                        </button>
                        <button
                          type="button"
                          onClick={() => skipRow(s)}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-white text-red-800 border border-red-200 hover:bg-red-50"
                          data-testid={`waba-vcf-surname-row-${si}-skip`}
                        >
                          Non importare
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showBulkProbableGiven && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
          role="dialog" aria-modal="true"
          data-testid="waba-vcf-bulk-probable-given-modal"
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5">
            <h3 className="font-serif text-xl text-ink-900 mb-2">
              Conferma automatica “Probabile nome”
            </h3>
            <p className="text-sm text-ink-700 mb-3">
              Confermare automaticamente <strong>{bulkProbableGivenCount}</strong> contatti
              classificati come <em>Probabile nome</em>?
            </p>
            <ul className="text-xs text-ink-600 mb-3 list-disc pl-4 space-y-1">
              <li>Nome proposto = valore già riconosciuto dall'allowlist italiana.</li>
              <li>Cognome proposto = vuoto (nome senza cognome).</li>
              <li>Note preservate se presenti · nessuna modifica.</li>
              <li>
                Nessun record già revisionato verrà modificato
                ({items.filter((it) => it.case_class === "PROBABLE_GIVEN_NAME"
                    && decisions[it.vcard_idx]).length} già confermati saltati).
              </li>
              {bulkProbableGivenSkippedDraft > 0 && (
                <li>
                  {bulkProbableGivenSkippedDraft} record con bozza attiva saranno
                  saltati (nessuna sovrascrittura).
                </li>
              )}
            </ul>
            <p className="text-xs text-ink-500 mb-4">
              Nessun dato viene ancora importato nel CRM · le decisioni
              restano locali fino al futuro APPLY.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowBulkProbableGiven(false)}
                className="px-3 py-2 rounded-full text-sm font-medium border border-ink-300 text-ink-700 hover:bg-ink-50"
                data-testid="waba-vcf-bulk-probable-given-cancel"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={doBulkConfirmProbableGiven}
                className="px-3 py-2 rounded-full text-sm font-semibold bg-sky-600 text-white hover:bg-sky-700"
                data-testid="waba-vcf-bulk-probable-given-confirm"
              >
                Conferma {bulkProbableGivenCount} contatti
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
          role="dialog" aria-modal="true"
          data-testid="waba-vcf-bulk-modal"
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5">
            <h3 className="font-serif text-xl text-ink-900 mb-2">
              Conferma suggerimenti sicuri
            </h3>
            <p className="text-sm text-ink-700 mb-3">
              Stai confermando <strong>{bulkPending}</strong> separazioni
              suggerite dal parser. I casi contrassegnati come "Da
              verificare manualmente" non sono inclusi in questa azione
              massiva.
            </p>
            <p className="text-xs text-ink-500 mb-4">
              Nessun dato verrà ancora importato nel CRM. Questa scelta
              rimane locale alla sessione fino a un futuro APPLY.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowBulkConfirm(false)}
                className="px-3 py-2 rounded-full text-sm font-medium border border-ink-300 text-ink-700 hover:bg-ink-50"
                data-testid="waba-vcf-bulk-cancel"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={doBulkConfirmSuggested}
                className="px-3 py-2 rounded-full text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="waba-vcf-bulk-confirm"
              >
                Conferma {bulkPending} suggerimenti
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// P0-6 FASE 2F.1 · Sezione "Contatti da verificare" con PII completa.
// Le decisioni sono locali (UI-only). Nessun POST/PATCH in questa fase.
function VcfReviewSection({ review, decisions, setDecisions, drafts, setDrafts,
                              collisionChoices, setCollisionChoices,
                              conflictChoices, setConflictChoices }) {
  const [surnameFilter, setSurnameFilter] = React.useState("all");
  const collisions = review?.collisions || [];
  const conflicts = review?.name_conflicts || [];
  const updates = review?.would_updates || [];
  const surnameAudit = review?.surname_audit || {};
  const surnameMissing = review?.surname_missing || [];
  const decorative = review?.decorative_emoji || [];
  const filteredSurnames = surnameMissing.filter((s) => {
    if (surnameFilter === "multiword") return s.has_multiword_given;
    if (surnameFilter === "singleword") return !s.has_multiword_given;
    return true;
  });
  const hasAnything = collisions.length || conflicts.length
    || updates.length || surnameMissing.length || decorative.length;
  if (!hasAnything) return null;

  return (
    <div className="mt-4 space-y-4" data-testid="waba-vcf-review-section">
      <h4 className="font-serif text-lg text-ink-900">
        Contatti da verificare
      </h4>
      <p className="text-xs text-ink-500">
        Contiene informazioni personali complete visibili solo al Super
        Admin autenticato. Nessuna decisione presa qui viene salvata o
        applicata: sono scelte locali di preparazione alla review.
      </p>

      {collisions.length > 0 && (
        <div
          className="rounded-lg border border-red-200 bg-red-50 overflow-hidden"
          data-testid="waba-vcf-review-collisions"
        >
          <div className="bg-red-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-red-800">
            Collisioni telefoniche · {collisions.length} gruppi
          </div>
          <div className="p-3 space-y-3">
            {collisions.map((g, gi) => (
              <div
                key={g.phone}
                className="rounded border border-red-200 bg-white p-3"
                data-testid={`waba-vcf-review-coll-${gi}`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className="font-mono text-sm text-red-900"
                    data-testid={`waba-vcf-review-coll-${gi}-phone`}
                  >
                    {g.phone}
                  </span>
                  <span className="text-xs text-ink-500">
                    · {g.cards_count} vCard
                  </span>
                  {g.waba_present && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"
                      data-testid={`waba-vcf-review-coll-${gi}-waba`}
                    >
                      Presente in WABA
                    </span>
                  )}
                  {g.crm_matches?.length > 0 && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200"
                      data-testid={`waba-vcf-review-coll-${gi}-crm`}
                    >
                      CRM: {g.crm_matches
                        .map((m) => `${m.name || ""} ${m.surname || ""}`
                          .trim() || "(senza nome)")
                        .join(" · ")}
                    </span>
                  )}
                </div>
                <table className="min-w-full text-xs">
                  <thead className="text-left text-ink-600">
                    <tr>
                      <th className="px-2 py-1">Nome</th>
                      <th className="px-2 py-1">Cognome</th>
                      <th className="px-2 py-1">FN</th>
                      <th className="px-2 py-1">TEL type</th>
                      <th className="px-2 py-1">PREF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.vcards.map((v, vi) => {
                      const t = v.tels?.find(
                        (x) => x.normalized === g.phone) || v.tels?.[0];
                      return (
                        <tr
                          key={v.vcard_idx}
                          className="border-t border-red-100"
                          data-testid={`waba-vcf-review-coll-${gi}-vc-${vi}`}
                        >
                          <td className="px-2 py-1">{v.name || "—"}</td>
                          <td className="px-2 py-1">{v.surname || "—"}</td>
                          <td className="px-2 py-1 text-ink-500">
                            {v.fn || "—"}
                          </td>
                          <td className="px-2 py-1 font-mono text-ink-500">
                            {t?.types?.join(",") || "—"}
                          </td>
                          <td className="px-2 py-1 font-mono text-ink-500">
                            {t?.pref ? "pref" : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="mt-2">
                  <label className="text-xs text-ink-600 mr-2">
                    Decisione (locale):
                  </label>
                  <select
                    className="text-xs border border-ink-200 rounded px-2 py-1"
                    value={collisionChoices[g.phone] || ""}
                    onChange={(e) => setCollisionChoices({
                      ...collisionChoices,
                      [g.phone]: e.target.value,
                    })}
                    data-testid={`waba-vcf-review-coll-${gi}-choice`}
                  >
                    <option value="">— nessuna decisione —</option>
                    {(g.suggested_options || []).map((op) => (
                      <option key={op} value={op}>
                        {OPTION_LABEL_IT[op] || op}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {conflicts.length > 0 && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 overflow-hidden"
          data-testid="waba-vcf-review-conflicts"
        >
          <div className="bg-amber-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-amber-900">
            Conflitti nome · {conflicts.length}
          </div>
          <div className="p-3 space-y-3">
            {conflicts.map((c, ci) => (
              <div
                key={c.crm.customer_id + "-" + c.vcard.vcard_idx}
                className="rounded border border-amber-200 bg-white p-3 grid md:grid-cols-2 gap-3"
                data-testid={`waba-vcf-review-conf-${ci}`}
              >
                <div>
                  <div className="text-xs text-amber-800 font-semibold mb-1">
                    Da VCF
                  </div>
                  <div className="text-sm">
                    {c.vcard.name || "—"}{" "}
                    <strong>{c.vcard.surname || "—"}</strong>
                  </div>
                  <div className="font-mono text-xs text-ink-500">
                    {c.vcard.phone}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-amber-800 font-semibold mb-1">
                    CRM esistente
                  </div>
                  <div className="text-sm">
                    {c.crm.name || "—"}{" "}
                    <strong>{c.crm.surname || "—"}</strong>
                  </div>
                  <div className="font-mono text-xs text-ink-500">
                    {c.crm.phone}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs text-ink-600 mr-2">
                    Decisione (locale):
                  </label>
                  <select
                    className="text-xs border border-ink-200 rounded px-2 py-1"
                    value={conflictChoices[c.crm.customer_id] || ""}
                    onChange={(e) => setConflictChoices({
                      ...conflictChoices,
                      [c.crm.customer_id]: e.target.value,
                    })}
                    data-testid={`waba-vcf-review-conf-${ci}-choice`}
                  >
                    <option value="">— nessuna decisione —</option>
                    {(c.suggested_options || []).map((op) => (
                      <option key={op} value={op}>
                        {OPTION_LABEL_IT[op] || op}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {updates.length > 0 && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 overflow-hidden"
          data-testid="waba-vcf-review-updates"
        >
          <div className="bg-blue-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-blue-900">
            Nome/cognome che verrebbero aggiornati · {updates.length}
          </div>
          <div className="p-3 space-y-2">
            {updates.map((u, ui) => (
              <div
                key={u.crm.customer_id + "-" + u.vcard.vcard_idx}
                className="rounded border border-blue-200 bg-white p-3 grid md:grid-cols-2 gap-3"
                data-testid={`waba-vcf-review-upd-${ui}`}
              >
                <div>
                  <div className="text-xs text-blue-800 font-semibold mb-1">
                    VCF (verrebbe applicato)
                  </div>
                  <div className="text-sm">
                    {u.vcard.name || "—"}{" "}
                    <strong>{u.vcard.surname || "—"}</strong>
                  </div>
                  <div className="font-mono text-xs text-ink-500">
                    {u.vcard.phone}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-blue-800 font-semibold mb-1">
                    CRM attuale
                  </div>
                  <div className="text-sm">
                    {u.crm.name || "—"}{" "}
                    <strong>{u.crm.surname || "—"}</strong>
                  </div>
                  <div className="font-mono text-xs text-ink-500">
                    {u.crm.phone}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* P0-6 FASE 2F.1B/C · Contatti senza cognome (revisione interattiva) */}
      {surnameMissing.length > 0 && (
        <SurnameMissingReview
          items={surnameMissing}
          audit={surnameAudit}
          decisions={decisions}
          setDecisions={setDecisions}
          drafts={drafts}
          setDrafts={setDrafts}
        />
      )}

      {/* P0-6 FASE 2F.1B · Contatti con simboli/emoji nel nome */}
      {decorative.length > 0 && (
        <div
          className="rounded-lg border border-pink-200 bg-pink-50 overflow-hidden"
          data-testid="waba-vcf-review-decorative"
        >
          <div className="bg-pink-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-pink-900">
            Contatti con simboli/emoji nel nome · {decorative.length}
          </div>
          <div className="p-3 max-h-96 overflow-auto">
            <table className="min-w-full text-xs">
              <thead className="text-left text-ink-600 sticky top-0 bg-pink-50">
                <tr>
                  <th className="px-2 py-1">N.given</th>
                  <th className="px-2 py-1">N.family</th>
                  <th className="px-2 py-1">FN</th>
                  <th className="px-2 py-1">Telefono</th>
                  <th className="px-2 py-1">Campi con emoji</th>
                </tr>
              </thead>
              <tbody data-testid="waba-vcf-decorative-list">
                {decorative.map((d, di) => {
                  const primaryTel = d.tels?.find((t) => t.valid)
                    || d.tels?.[0];
                  return (
                    <tr
                      key={d.vcard_idx}
                      className="border-t border-pink-100"
                      data-testid={`waba-vcf-decorative-row-${di}`}
                    >
                      <td className="px-2 py-1">{d.n_given || "—"}</td>
                      <td className="px-2 py-1">{d.n_family || "—"}</td>
                      <td className="px-2 py-1 text-ink-500">
                        {d.fn || "—"}
                      </td>
                      <td className="px-2 py-1 font-mono">
                        {primaryTel?.normalized || primaryTel?.raw || "—"}
                      </td>
                      <td className="px-2 py-1 font-mono text-ink-500">
                        {(d.decorative_fields || []).join(", ") || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function WabaDirectoryBootstrapPanel() {  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [report, setReport] = useState(null);

  // APPLY state
  const [showConfirm, setShowConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyErr, setApplyErr] = useState(null);
  const [applyResult, setApplyResult] = useState(null);

  // P0-6 FASE 2D-HISTORICAL state
  const [hist, setHist] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histErr, setHistErr] = useState(null);
  const [showHistConfirm, setShowHistConfirm] = useState(false);
  const [histActing, setHistActing] = useState(false);

  const loadHistStatus = useCallback(async () => {
    setHistErr(null);
    try {
      const r = await api.get(
        "/admin/messaging/waba-directory/historical-sync/status");
      setHist(r.data);
    } catch (e) {
      setHistErr(friendlyError(e));
    }
  }, []);

  React.useEffect(() => { if (isSuper) loadHistStatus(); },
    [isSuper, loadHistStatus]);

  // Auto-refresh while active
  React.useEffect(() => {
    if (!hist?.historical_mode_active) return;
    const t = setInterval(loadHistStatus, 5000);
    return () => clearInterval(t);
  }, [hist?.historical_mode_active, loadHistStatus]);

  const startHistSync = useCallback(async () => {
    if (histActing) return;
    setHistActing(true); setHistErr(null);
    try {
      await api.post(
        "/admin/messaging/waba-directory/historical-sync/start");
      setShowHistConfirm(false);
      await loadHistStatus();
    } catch (e) {
      setHistErr(friendlyError(e));
    } finally {
      setHistActing(false);
    }
  }, [histActing, loadHistStatus]);

  const endHistSync = useCallback(async () => {
    if (histActing) return;
    if (!window.confirm(
      "Confermi di terminare la sincronizzazione storica?")) return;
    setHistActing(true); setHistErr(null);
    try {
      await api.post(
        "/admin/messaging/waba-directory/historical-sync/end");
      await loadHistStatus();
    } catch (e) {
      setHistErr(friendlyError(e));
    } finally {
      setHistActing(false);
    }
  }, [histActing, loadHistStatus]);

  const runDryRun = useCallback(async () => {
    setLoading(true);
    setErr(null);
    setReport(null);
    setApplyResult(null);
    setApplyErr(null);
    try {
      const r = await api.get(
        "/admin/messaging/waba-directory/bootstrap-dry-run",
      );
      setReport(r.data);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const runApply = useCallback(async () => {
    if (applying) return;  // double-click guard
    setApplying(true);
    setApplyErr(null);
    setApplyResult(null);
    try {
      const r = await api.post(
        "/admin/messaging/waba-directory/bootstrap-apply",
      );
      setApplyResult(r.data);
      setShowConfirm(false);
      // Auto-refresh dry-run per mostrare stato post-importazione
      try {
        const r2 = await api.get(
          "/admin/messaging/waba-directory/bootstrap-dry-run",
        );
        setReport(r2.data);
      } catch (_) {
        /* non fatale */
      }
    } catch (e) {
      setApplyErr(friendlyError(e));
    } finally {
      setApplying(false);
    }
  }, [applying]);

  // P0-6 FASE 2F · VCF import state
  const [vcfFile, setVcfFile] = useState(null);
  const [vcfLoading, setVcfLoading] = useState(false);
  const [vcfErr, setVcfErr] = useState(null);
  const [vcfReport, setVcfReport] = useState(null);

  // P0-6 FASE 2F.1G · Persistenza review VCF
  const [vcfFingerprint, setVcfFingerprint] = useState(null);
  const [reviewDecisions, setReviewDecisions] = useState({});
  const [reviewDrafts, setReviewDrafts] = useState({});
  const [reviewCollisions, setReviewCollisions] = useState({});
  const [reviewConflicts, setReviewConflicts] = useState({});
  const [restoreBanner, setRestoreBanner] = useState(null);
  const [importError, setImportError] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const importInputRef = useRef(null);
  const skipNextSaveRef = useRef(false);

  // P0-6 FASE 2G · PRE-APPLY finale (read-only, no writes)
  const [preApplyLoading, setPreApplyLoading] = useState(false);
  const [preApplyReport, setPreApplyReport] = useState(null);
  const [preApplyErr, setPreApplyErr] = useState(null);
  // P0-6 FASE 2G · APPLY reale VCF (scrive customers, idempotente)
  const [vcfApplyLoading, setVcfApplyLoading] = useState(false);
  const [vcfApplyResult, setVcfApplyResult] = useState(null);
  const [vcfApplyErr, setVcfApplyErr] = useState(null);
  const [showApplyConfirm1, setShowApplyConfirm1] = useState(false);
  const [showApplyConfirm2, setShowApplyConfirm2] = useState(false);
  const [applyConfirmText, setApplyConfirmText] = useState("");

  const runPreApply = useCallback(async () => {
    if (!vcfFile || !vcfReport || !vcfFingerprint) {
      setPreApplyErr("Analizza prima il VCF.");
      return;
    }
    setPreApplyLoading(true);
    setPreApplyErr(null);
    setPreApplyReport(null);
    try {
      // Recupera fingerprint del backup dallo storage (se presente)
      const stored = loadFromStorage(vcfFingerprint);
      const form = new FormData();
      form.append("file", vcfFile);
      form.append("decisions_payload", JSON.stringify({
        decisions: reviewDecisions,
        drafts: reviewDrafts,
        collisionChoices: reviewCollisions,
        conflictChoices: reviewConflicts,
        current_fingerprint: vcfFingerprint,
        backup_fingerprint: stored?.vcf_fingerprint || vcfFingerprint,
      }));
      const r = await api.post(
        "/admin/messaging/vcf-bootstrap/pre-apply",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreApplyReport(r.data);
    } catch (e) {
      setPreApplyErr(friendlyError(e));
    } finally {
      setPreApplyLoading(false);
    }
  }, [vcfFile, vcfReport, vcfFingerprint, reviewDecisions, reviewDrafts,
      reviewCollisions, reviewConflicts]);

  const runVcfApply = useCallback(async () => {
    if (!vcfFile || !vcfReport || !vcfFingerprint) return;
    if (!preApplyReport || preApplyReport.status !== "READY_FOR_APPLY") {
      setVcfApplyErr("PRE-APPLY non è pronto: rigenera prima.");
      return;
    }
    setVcfApplyLoading(true); setVcfApplyErr(null); setVcfApplyResult(null);
    try {
      const stored = loadFromStorage(vcfFingerprint);
      const form = new FormData();
      form.append("file", vcfFile);
      form.append("decisions_payload", JSON.stringify({
        decisions: reviewDecisions, drafts: reviewDrafts,
        collisionChoices: reviewCollisions,
        conflictChoices: reviewConflicts,
        current_fingerprint: vcfFingerprint,
        backup_fingerprint: stored?.vcf_fingerprint || vcfFingerprint,
      }));
      form.append("confirm_token", `APPLY-${vcfFingerprint.slice(0, 12)}`);
      const r = await api.post(
        "/admin/messaging/vcf-bootstrap/apply", form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setVcfApplyResult(r.data);
      setShowApplyConfirm1(false);
      setShowApplyConfirm2(false);
      setApplyConfirmText("");
    } catch (e) {
      setVcfApplyErr(friendlyError(e));
    } finally {
      setVcfApplyLoading(false);
    }
  }, [vcfFile, vcfReport, vcfFingerprint, preApplyReport, reviewDecisions,
      reviewDrafts, reviewCollisions, reviewConflicts]);

  const runVcfDryRun = useCallback(async () => {
    if (!vcfFile || vcfLoading) return;
    setVcfLoading(true);
    setVcfErr(null);
    setVcfReport(null);
    setVcfFingerprint(null);
    setRestoreBanner(null);
    setImportError(null);
    try {
      // Fingerprint SHA-256 del file PRIMA dell'upload (deterministico,
      // sopravvive a refresh/deploy — chiave persistenza localStorage).
      const fp = await sha256File(vcfFile);
      const form = new FormData();
      form.append("file", vcfFile);
      const r = await api.post(
        "/admin/messaging/vcf-bootstrap/dry-run",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      // Nuovo VCF: azzera lo state prima di applicare l'auto-restore.
      skipNextSaveRef.current = true;
      setReviewDecisions({});
      setReviewDrafts({});
      setReviewCollisions({});
      setReviewConflicts({});
      setVcfReport(r.data);
      setVcfFingerprint(fp);
    } catch (e) {
      setVcfErr(friendlyError(e));
    } finally {
      setVcfLoading(false);
    }
  }, [vcfFile, vcfLoading]);

  // Auto-restore dallo storage quando fingerprint cambia (nuovo VCF analizzato)
  useEffect(() => {
    if (!vcfFingerprint) return;
    const stored = loadFromStorage(vcfFingerprint);
    if (stored) {
      skipNextSaveRef.current = true;
      setReviewDecisions(stored.decisions || {});
      setReviewDrafts(stored.drafts || {});
      setReviewCollisions(stored.collisionChoices || {});
      setReviewConflicts(stored.conflictChoices || {});
      setRestoreBanner({
        source: "auto",
        counts: stored.counts || {
          decisions: Object.keys(stored.decisions || {}).length,
          drafts: Object.keys(stored.drafts || {}).length,
          collisionChoices: Object.keys(stored.collisionChoices || {}).length,
          conflictChoices: Object.keys(stored.conflictChoices || {}).length,
        },
        exported_at: stored.exported_at || null,
      });
    }
  }, [vcfFingerprint]);

  // Autosave debounced 300ms su ogni cambio state review
  useEffect(() => {
    if (!vcfFingerprint) return;
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    const t = setTimeout(() => {
      saveToStorage(
        vcfFingerprint,
        {
          decisions: reviewDecisions,
          drafts: reviewDrafts,
          collisionChoices: reviewCollisions,
          conflictChoices: reviewConflicts,
        },
        extractVcfMeta(vcfReport, vcfFile),
      );
    }, 300);
    return () => clearTimeout(t);
  }, [vcfFingerprint, reviewDecisions, reviewDrafts, reviewCollisions,
      reviewConflicts, vcfReport, vcfFile]);

  const handleImportBackup = useCallback(async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setImportError(null);
    if (!vcfReport || !vcfFingerprint) {
      setImportError("Analizza prima il VCF, poi ripristina il backup.");
      if (importInputRef.current) importInputRef.current.value = "";
      return;
    }
    try {
      const text = await file.text();
      let json;
      try { json = JSON.parse(text); }
      catch { throw new Error("File non è JSON valido"); }
      const shape = validateBackupShape(json);
      if (!shape.ok) throw new Error(shape.reason);
      const compat = backupMatchesCurrentVcf(
        json, vcfReport, vcfFile, vcfFingerprint);
      if (!compat.compatible) {
        throw new Error("Il backup non corrisponde al VCF corrente · "
          + compat.reason);
      }
      skipNextSaveRef.current = false; // vogliamo salvare subito il restore
      setReviewDecisions(json.decisions);
      setReviewDrafts(json.drafts);
      setReviewCollisions(json.collisionChoices);
      setReviewConflicts(json.conflictChoices);
      setRestoreBanner({
        source: "import",
        counts: {
          decisions: Object.keys(json.decisions).length,
          drafts: Object.keys(json.drafts).length,
          collisionChoices: Object.keys(json.collisionChoices).length,
          conflictChoices: Object.keys(json.conflictChoices).length,
        },
        exported_at: json.exported_at || null,
        warnings: compat.warnings || null,
      });
    } catch (err) {
      setImportError(err?.message || String(err));
    } finally {
      if (importInputRef.current) importInputRef.current.value = "";
    }
  }, [vcfReport, vcfFile, vcfFingerprint]);

  const handleResetReview = useCallback(() => {
    if (!vcfFingerprint) return;
    resetStorage(vcfFingerprint);
    setReviewDecisions({});
    setReviewDrafts({});
    setReviewCollisions({});
    setReviewConflicts({});
    setRestoreBanner(null);
    setShowResetConfirm(false);
  }, [vcfFingerprint]);

  const hasAnyReviewState =
    Object.keys(reviewDecisions).length > 0
    || Object.keys(reviewDrafts).length > 0
    || Object.keys(reviewCollisions).length > 0
    || Object.keys(reviewConflicts).length > 0;

  const histActive = hist?.historical_mode_active;
  const applyBlocked = histActive;

  if (!isSuper) return null;

  return (
    <section
      className="card-elevated p-5 mt-6"
      data-testid="waba-bootstrap-panel"
      id="waba-bootstrap-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <h2
            className="font-serif text-2xl text-ink-900"
            data-testid="waba-bootstrap-title"
          >
            Importazione rubrica WhatsApp
          </h2>
          <p className="text-sm text-ink-600 mt-1">
            Analizza i contatti WhatsApp Business già sincronizzati e
            verifica quali clienti verrebbero collegati, creati o aggiornati
            nel CRM. L'analisi non modifica alcun dato.
          </p>
        </div>
        <button
          type="button"
          onClick={runDryRun}
          disabled={loading}
          className={
            "px-4 py-2 rounded-full font-medium text-sm transition-colors " +
            (loading
              ? "bg-ink-200 text-ink-500 cursor-not-allowed"
              : "bg-bronze-600 text-white hover:bg-bronze-700")
          }
          data-testid="waba-bootstrap-run-btn"
        >
          {loading ? "Analisi in corso…" : "Esegui analisi"}
        </button>
      </div>

      {/* P0-6 FASE 2D-HISTORICAL · Sincronizzazione rubrica dal cellulare */}
      <div
        className="mt-5 rounded-lg border border-ink-100 bg-white overflow-hidden"
        data-testid="waba-hist-block"
      >
        <div className="bg-ink-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3
              className="font-serif text-lg text-ink-900"
              data-testid="waba-hist-title"
            >
              1 · Sincronizzazione rubrica WhatsApp
            </h3>
            <p className="text-xs text-ink-600 mt-0.5">
              Richiede a WhatsApp Business di ritrasmettere l'elenco
              completo dei contatti salvati sul cellulare aziendale. Durante
              la sincronizzazione le scritture live sul CRM sono sospese.
            </p>
          </div>
        </div>

        {histErr && (
          <div
            className="mx-4 mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
            data-testid="waba-hist-error"
            role="alert"
          >
            {histErr}
          </div>
        )}

        {!hist && !histErr && (
          <div
            className="p-4 text-sm text-ink-500"
            data-testid="waba-hist-loading"
          >
            Caricamento stato…
          </div>
        )}

        {hist && (
          <div className="p-4 space-y-4">
            <IncrementalSyncStatus inc={hist.incremental} />
            {/* Badges di stato · SEMPRE visibili prima di qualsiasi azione */}
            <div
              className="flex flex-wrap gap-2 items-center"
              data-testid="waba-hist-badges"
            >
              <span
                className={
                  "text-xs px-2 py-1 rounded-full border font-medium " +
                  (hist.master_enabled
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : "bg-ink-50 text-ink-600 border-ink-200")
                }
                data-testid="waba-hist-badge-master"
              >
                Funzione storica:{" "}
                {hist.master_enabled ? "ABILITATA" : "DISABILITATA"}
              </span>
              <span
                className={
                  "text-xs px-2 py-1 rounded-full border font-medium " +
                  (hist.meta_mock
                    ? "bg-amber-50 text-amber-800 border-amber-300"
                    : "bg-rose-50 text-rose-800 border-rose-300")
                }
                data-testid="waba-hist-badge-meta-mode"
              >
                Meta Mode: {hist.meta_mock ? "MOCK" : "LIVE"}
              </span>
              <span
                className={
                  "text-xs px-2 py-1 rounded-full border font-medium " +
                  (hist.crm_writes_suppressed
                    ? "bg-orange-50 text-orange-800 border-orange-300"
                    : "bg-ink-50 text-ink-600 border-ink-200")
                }
                data-testid="waba-hist-badge-crm-suppression"
              >
                CRM suppression:{" "}
                {hist.crm_writes_suppressed ? "ACTIVE" : "INACTIVE"}
              </span>
              <span
                className={
                  "text-xs px-2 py-1 rounded-full border font-medium " +
                  (hist.historical_mode_active
                    ? "bg-blue-50 text-blue-800 border-blue-300"
                    : "bg-ink-50 text-ink-700 border-ink-200")
                }
                data-testid="waba-hist-badge-status"
              >
                Ultima sessione storica: {hist.status || "NESSUNA"}
              </span>
            </div>

            {/* Meta Mode banner · impossibile da equivocare */}
            {hist.master_enabled && (
              hist.meta_mock ? (
                <div
                  className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"
                  data-testid="waba-hist-meta-mock-banner"
                >
                  <strong>TEST</strong> · Nessuna chiamata reale a Meta.
                  L'avvio della sincronizzazione crea una sessione locale
                  senza inviare nulla a WhatsApp Business.
                </div>
              ) : (
                <div
                  className="rounded-md bg-rose-50 border border-rose-300 px-3 py-2 text-sm text-rose-900"
                  data-testid="waba-hist-meta-live-banner"
                >
                  <strong>LIVE</strong> · La conferma invierà una richiesta
                  reale a WhatsApp Business. Meta consente una sola
                  chiamata di questo tipo per finestra: usare con cura.
                </div>
              )
            )}

            {/* Counters directory */}
            <div className="grid sm:grid-cols-3 gap-2 text-xs">
              <div
                className="rounded border border-ink-100 bg-ink-50/60 px-3 py-2"
                data-testid="waba-hist-dir-total"
              >
                <div className="text-ink-600">Directory totale</div>
                <div className="font-mono text-lg text-ink-900">
                  {hist.directory_total_now ?? 0}
                </div>
              </div>
              <div
                className="rounded border border-ink-100 bg-ink-50/60 px-3 py-2"
                data-testid="waba-hist-dir-active"
              >
                <div className="text-ink-600">Directory attiva</div>
                <div className="font-mono text-lg text-ink-900">
                  {hist.directory_active_now ?? 0}
                </div>
              </div>
              <div
                className="rounded border border-ink-100 bg-ink-50/60 px-3 py-2"
                data-testid="waba-hist-last-sync"
              >
                <div className="text-ink-600">Ultima sessione storica</div>
                <div className="font-mono text-xs text-ink-900 truncate">
                  {hist.requested_at ? fmtRome(hist.requested_at) : "—"}
                </div>
              </div>
            </div>

            {/* Live counters durante sessione ACTIVE */}
            {hist.historical_mode_active && (
              <div
                className="grid sm:grid-cols-4 gap-2 text-xs"
                data-testid="waba-hist-live-counters"
              >
                <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2">
                  <div className="text-blue-700">Eventi ricevuti</div>
                  <div className="font-mono text-lg text-blue-900">
                    {hist.events_received ?? 0}
                  </div>
                </div>
                <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2">
                  <div className="text-blue-700">Contatti unici</div>
                  <div className="font-mono text-lg text-blue-900">
                    {hist.unique_contacts_by_user_id ?? 0}
                  </div>
                </div>
                <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2">
                  <div className="text-blue-700">Eventi/min</div>
                  <div className="font-mono text-lg text-blue-900">
                    {hist.events_per_minute ?? "—"}
                  </div>
                </div>
                <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2">
                  <div className="text-blue-700">Durata (s)</div>
                  <div className="font-mono text-lg text-blue-900">
                    {hist.elapsed_seconds ?? 0}
                  </div>
                </div>
              </div>
            )}

            {/* P0-6 FASE 2D-DIAG · box ultimo tentativo FAILED (sanitizzato) */}
            {hist.status === "FAILED" && hist.last_failed && (
              <details
                className="rounded-lg border border-ink-200 bg-ink-50/60 overflow-hidden"
                data-testid="waba-hist-last-failed"
              >
                <summary
                  className="cursor-pointer px-3 py-2 text-xs font-semibold text-ink-700"
                  data-testid="waba-hist-last-failed-toggle"
                >
                  Diagnostica · ultima sincronizzazione storica non riuscita
                  {hist.last_failed.code != null ? ` (errore Meta #${hist.last_failed.code})` : ""}
                  {" — non indica un guasto degli aggiornamenti incrementali"}
                </summary>
                <div className="p-3 text-sm text-red-900 space-y-1.5 border-t border-ink-200 bg-white">
                  {hist.last_failed.requested_at && (
                    <div data-testid="waba-hist-failed-requested-at">
                      <span className="text-red-700">Data/ora:</span>{" "}
                      <span className="font-mono">
                        {fmtRome(hist.last_failed.requested_at)}
                      </span>
                    </div>
                  )}
                  <div data-testid="waba-hist-failed-status">
                    <span className="text-red-700">Stato:</span>{" "}
                    <span className="font-mono">FAILED</span>
                  </div>
                  {hist.last_failed.http_status != null && (
                    <div data-testid="waba-hist-failed-http-status">
                      <span className="text-red-700">HTTP status:</span>{" "}
                      <span className="font-mono">
                        {hist.last_failed.http_status}
                      </span>
                    </div>
                  )}
                  {hist.last_failed.code != null && (
                    <div data-testid="waba-hist-failed-code">
                      <span className="text-red-700">Meta error code:</span>{" "}
                      <span className="font-mono">
                        {hist.last_failed.code}
                      </span>
                    </div>
                  )}
                  {hist.last_failed.subcode != null && (
                    <div data-testid="waba-hist-failed-subcode">
                      <span className="text-red-700">
                        Meta error subcode:
                      </span>{" "}
                      <span className="font-mono">
                        {hist.last_failed.subcode}
                      </span>
                    </div>
                  )}
                  {hist.last_failed.type && (
                    <div data-testid="waba-hist-failed-type">
                      <span className="text-red-700">Tipo errore:</span>{" "}
                      <span className="font-mono">
                        {hist.last_failed.type}
                      </span>
                    </div>
                  )}
                  {hist.last_failed.message && (
                    <div data-testid="waba-hist-failed-message">
                      <span className="text-red-700">Messaggio Meta:</span>{" "}
                      <span className="font-mono break-words">
                        {hist.last_failed.message}
                      </span>
                    </div>
                  )}
                  {hist.last_failed.fbtrace_id && (
                    <div data-testid="waba-hist-failed-fbtrace">
                      <span className="text-red-700">fbtrace_id:</span>{" "}
                      <span className="font-mono">
                        {hist.last_failed.fbtrace_id}
                      </span>
                    </div>
                  )}
                  {hist.last_failed.stage && (
                    <div data-testid="waba-hist-failed-stage">
                      <span className="text-red-700">Failure stage:</span>{" "}
                      <span className="font-mono">
                        {hist.last_failed.stage}
                      </span>
                    </div>
                  )}
                  {/* Side-effect verificabili */}
                  <div
                    className="pt-2 mt-2 border-t border-red-200"
                    data-testid="waba-hist-failed-side-effects"
                  >
                    {hist.last_failed.directory_delta === 0
                    && hist.last_failed.contact_events_since_requested_at
                       === 0 ? (
                      <>
                        <div data-testid="waba-hist-failed-directory-no">
                          Directory modificata:{" "}
                          <span className="font-semibold">NO</span>
                        </div>
                        <div data-testid="waba-hist-failed-crm-no">
                          CRM modificato:{" "}
                          <span className="font-semibold">NO</span>
                        </div>
                      </>
                    ) : (
                      <div data-testid="waba-hist-failed-side-effect-unknown">
                        Nessuna modifica rilevata sulla base dei
                        contatori attualmente disponibili.
                      </div>
                    )}
                  </div>
                </div>
              </details>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-3">
              {!hist.master_enabled && (
                <span
                  className="text-xs text-ink-500"
                  data-testid="waba-hist-disabled-hint"
                >
                  Historical Sync disabilitato via configurazione ambiente.
                </span>
              )}
              {hist.master_enabled && !hist.historical_mode_active && (
                <button
                  type="button"
                  onClick={() => setShowHistConfirm(true)}
                  disabled={histActing}
                  className={
                    "px-4 py-2 rounded-full font-medium text-sm " +
                    (histActing
                      ? "bg-ink-200 text-ink-500 cursor-not-allowed"
                      : "bg-indigo-700 text-white hover:bg-indigo-800")
                  }
                  data-testid="waba-hist-start-btn"
                >
                  Avvia sincronizzazione rubrica
                </button>
              )}
              {hist.master_enabled && !hist.historical_mode_active && (
                <span className="text-xs text-ink-500" data-testid="waba-hist-start-hint">
                  Serve solo per un nuovo import storico completo: durante la sessione
                  le scritture CRM live sono sospese. Non necessario per gli aggiornamenti incrementali.
                </span>
              )}
              {hist.historical_mode_active && (
                <button
                  type="button"
                  onClick={endHistSync}
                  disabled={histActing}
                  className={
                    "px-4 py-2 rounded-full font-medium text-sm " +
                    (histActing
                      ? "bg-ink-200 text-ink-500 cursor-not-allowed"
                      : "bg-red-700 text-white hover:bg-red-800")
                  }
                  data-testid="waba-hist-end-btn"
                >
                  Termina sincronizzazione
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* P0-6 FASE 2F · Importazione rubrica iPhone (VCF DRY-RUN) */}
      <div
        className="mt-5 rounded-lg border border-ink-100 bg-white overflow-hidden"
        data-testid="waba-vcf-block"
      >
        <div className="bg-ink-50 px-4 py-3">
          <h3
            className="font-serif text-lg text-ink-900"
            data-testid="waba-vcf-title"
          >
            2 · Importazione rubrica iPhone
          </h3>
          <p className="text-xs text-ink-600 mt-0.5">
            Carica il file <code>.vcf</code> esportato dall'app Contatti
            del cellulare aziendale. Verrà eseguita SOLO un'analisi
            (DRY-RUN) senza modificare CRM, prenotazioni, conversazioni o
            directory WhatsApp. L'importazione effettiva sarà una fase
            successiva soggetta ad approvazione.
          </p>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".vcf,text/vcard,text/x-vcard"
              onChange={(e) => {
                setVcfFile(e.target.files?.[0] || null);
                setVcfReport(null);
                setVcfErr(null);
              }}
              disabled={vcfLoading}
              data-testid="waba-vcf-file-input"
              className="text-sm"
            />
            <button
              type="button"
              onClick={runVcfDryRun}
              disabled={!vcfFile || vcfLoading}
              className={
                "px-4 py-2 rounded-full font-medium text-sm " +
                (!vcfFile || vcfLoading
                  ? "bg-ink-200 text-ink-500 cursor-not-allowed"
                  : "bg-bronze-600 text-white hover:bg-bronze-700")
              }
              data-testid="waba-vcf-analyze-btn"
            >
              {vcfLoading ? "Analisi VCF in corso…" : "Analizza VCF"}
            </button>
            {vcfFile && (
              <span
                className="text-xs text-ink-500 font-mono truncate max-w-xs"
                data-testid="waba-vcf-filename"
              >
                {vcfFile.name} · {(vcfFile.size / 1024).toFixed(0)} KB
              </span>
            )}
          </div>

          {vcfErr && (
            <div
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
              data-testid="waba-vcf-error"
              role="alert"
            >
              {vcfErr}
            </div>
          )}

          {vcfReport && (
            <div className="space-y-3" data-testid="waba-vcf-report">
              <div className="flex flex-wrap items-center gap-2">
                {vcfReport.final?.safe_to_apply ? (
                  <span
                    className="text-xs px-2 py-1 rounded-full font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
                    data-testid="waba-vcf-safe-badge"
                  >
                    Analisi superata · Import teoricamente sicuro
                  </span>
                ) : (
                  <span
                    className="text-xs px-2 py-1 rounded-full font-medium bg-red-100 text-red-800 border border-red-200"
                    data-testid="waba-vcf-unsafe-badge"
                  >
                    Analisi con avvisi · Import NON sicuro
                  </span>
                )}
                <span
                  className="text-xs text-ink-500 font-mono truncate"
                  data-testid="waba-vcf-file-info"
                >
                  {vcfReport.file?.filename} ·{" "}
                  {vcfReport.file?.vcard_total} vCard ·{" "}
                  {vcfReport.file?.file_size_bytes} bytes
                </span>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                {/* CONTACTS + PHONES */}
                <div
                  className="rounded border border-ink-100 bg-white overflow-hidden"
                  data-testid="waba-vcf-contacts-block"
                >
                  <div className="bg-ink-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-700">
                    Contatti + Telefoni
                  </div>
                  <div>
                    {[
                      ["contacts_total", "Contatti totali",
                        vcfReport.contacts],
                      ["contacts_with_phone", "Con telefono",
                        vcfReport.contacts],
                      ["contacts_without_phone", "Senza telefono",
                        vcfReport.contacts],
                      ["contacts_with_multiple_phones",
                        "Con telefoni multipli", vcfReport.contacts],
                      ["total_phone_numbers", "Numeri totali",
                        vcfReport.contacts],
                      ["valid_e164", "E.164 validi", vcfReport.phones],
                      ["invalid", "Non validi", vcfReport.phones],
                      ["unique_valid_phones", "Unici validi",
                        vcfReport.phones],
                      ["duplicates_inside_vcf",
                        "Duplicati interni", vcfReport.phones],
                    ].map(([k, label, src]) => (
                      <StatRow
                        key={k}
                        label={label}
                        value={src?.[k]}
                        danger={k === "invalid"
                          || k === "duplicates_inside_vcf"}
                        testId={`waba-vcf-${k}`}
                      />
                    ))}
                  </div>
                </div>

                {/* CRM SIMULATION */}
                <div
                  className="rounded border border-ink-100 bg-white overflow-hidden"
                  data-testid="waba-vcf-crm-block"
                >
                  <div className="bg-ink-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-700">
                    Simulazione CRM
                  </div>
                  <div>
                    {[
                      ["WOULD_LINK_EXISTING", "Da collegare"],
                      ["WOULD_CREATE", "Da creare"],
                      ["WOULD_UPDATE_NAME", "Nome da aggiornare"],
                      ["NAME_CONFLICT_REVIEW", "Conflitto nome (REVIEW)"],
                      ["WOULD_NOOP", "Invariati"],
                      ["COLLISION", "Collisioni"],
                      ["SKIPPED_INVALID",
                        "Ignorati (telefono non valido)"],
                      ["SKIPPED_NO_PHONE",
                        "Ignorati (senza telefono)"],
                    ].map(([k, label]) => (
                      <StatRow
                        key={k}
                        label={label}
                        value={vcfReport.crm_simulation?.[k]}
                        danger={k === "COLLISION"
                          || k === "NAME_CONFLICT_REVIEW"}
                        testId={`waba-vcf-crm-${k}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* WABA diagnostic + FINAL */}
              <div className="grid md:grid-cols-2 gap-3">
                <div
                  className="rounded border border-ink-100 bg-white overflow-hidden"
                  data-testid="waba-vcf-waba-block"
                >
                  <div className="bg-ink-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-700">
                    Confronto directory WhatsApp
                  </div>
                  <div>
                    <StatRow
                      label="Presenti in directory WABA"
                      value={vcfReport.waba_diagnostic
                        ?.MATCH_WABA_DIRECTORY}
                      testId="waba-vcf-waba-match"
                    />
                    <StatRow
                      label="Non presenti"
                      value={vcfReport.waba_diagnostic
                        ?.NOT_IN_WABA_DIRECTORY}
                      testId="waba-vcf-waba-nomatch"
                    />
                  </div>
                </div>
                <div
                  className="rounded border border-ink-100 bg-white overflow-hidden"
                  data-testid="waba-vcf-final-block"
                >
                  <div className="bg-ink-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-700">
                    Risultato finale
                  </div>
                  <div>
                    <StatRow
                      label="Clienti CRM attuali"
                      value={vcfReport.final?.customers_before}
                      testId="waba-vcf-final-before"
                    />
                    <StatRow
                      label="Previsti dopo APPLY futuro"
                      value={vcfReport.final?.customers_after_expected}
                      testId="waba-vcf-final-after"
                    />
                    <StatRow
                      label="Nuovi clienti netti previsti"
                      value={vcfReport.final?.net_new_customers_expected}
                      testId="waba-vcf-final-new"
                    />
                    <StatRow
                      label="Errori"
                      value={vcfReport.final?.errors}
                      danger
                      testId="waba-vcf-final-errors"
                    />
                  </div>
                </div>
              </div>

              <div className="text-xs text-ink-500">
                Nessun pulsante di importazione è disponibile in questa
                fase. L'analisi è puramente diagnostica e non modifica
                nessun dato.
              </div>

              {/* P0-6 FASE 2F.1G · Persistenza review + import backup */}
              {isSuper && (
                <div
                  className="rounded-lg border border-ink-100 bg-white p-3 flex flex-wrap items-center gap-3"
                  data-testid="waba-vcf-persistence-controls"
                >
                  <div className="text-xs text-ink-600 flex-1 min-w-[240px]">
                    <div>
                      <strong>Ripristino review</strong> · le decisioni
                      admin vengono salvate automaticamente nel browser
                      (chiave locale SHA-256 del VCF).
                    </div>
                    {vcfFingerprint && (
                      <div
                        className="mt-1 font-mono text-ink-400"
                        data-testid="waba-vcf-fingerprint"
                      >
                        fingerprint: {vcfFingerprint.slice(0, 16)}…
                      </div>
                    )}
                  </div>
                  <label
                    className="cursor-pointer px-3 py-2 rounded-full text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700"
                    data-testid="waba-vcf-import-backup-btn"
                  >
                    Ripristina review da backup
                    <input
                      ref={importInputRef}
                      type="file"
                      accept=".json,application/json"
                      className="hidden"
                      onChange={handleImportBackup}
                      data-testid="waba-vcf-import-backup-input"
                    />
                  </label>
                  {hasAnyReviewState && (
                    <button
                      type="button"
                      onClick={() => setShowResetConfirm(true)}
                      className="px-3 py-2 rounded-full text-sm font-semibold bg-red-100 text-red-800 hover:bg-red-200 border border-red-200"
                      data-testid="waba-vcf-reset-review-btn"
                    >
                      Resetta review salvata
                    </button>
                  )}
                </div>
              )}
              {importError && (
                <div
                  className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
                  data-testid="waba-vcf-import-error"
                >
                  {importError}
                </div>
              )}
              {restoreBanner && (
                <div
                  className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
                  data-testid="waba-vcf-restore-banner"
                >
                  <strong>Review ripristinata</strong> ·
                  {" "}{restoreBanner.counts.decisions} decisioni ·
                  {" "}{restoreBanner.counts.drafts} bozze ·
                  {" "}{restoreBanner.counts.collisionChoices} collisioni ·
                  {" "}{restoreBanner.counts.conflictChoices} conflitti
                  {restoreBanner.source === "import" && " · da file"}
                  {restoreBanner.source === "auto"
                    && " · dal salvataggio locale automatico"}
                  {restoreBanner.warnings && (
                    <div
                      className="mt-1 text-xs text-emerald-800"
                      data-testid="waba-vcf-restore-warnings"
                    >
                      ⚠ Avviso: {restoreBanner.warnings.join(" · ")}
                    </div>
                  )}
                </div>
              )}
              {showResetConfirm && (
                <div
                  className="rounded-md border border-red-400 bg-red-50 px-4 py-3 space-y-2"
                  data-testid="waba-vcf-reset-confirm"
                >
                  <div className="text-sm text-red-900 font-semibold">
                    Confermi il reset della review?
                  </div>
                  <div className="text-xs text-red-800">
                    Verranno cancellate tutte le decisioni, bozze,
                    collisioni e conflitti salvati per questo VCF nel
                    browser. Azione irreversibile.
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowResetConfirm(false)}
                      className="px-3 py-1.5 rounded-full text-sm bg-white border border-red-300 text-red-800 hover:bg-red-100"
                      data-testid="waba-vcf-reset-cancel"
                    >
                      Annulla
                    </button>
                    <button
                      type="button"
                      onClick={handleResetReview}
                      className="px-3 py-1.5 rounded-full text-sm font-semibold bg-red-600 text-white hover:bg-red-700"
                      data-testid="waba-vcf-reset-confirm-btn"
                    >
                      Sì, resetta
                    </button>
                  </div>
                </div>
              )}

              {/* P0-6 FASE 2F.1 · Contatti da verificare (PII completa) */}
              {vcfReport.review && (
                <VcfReviewSection
                  review={vcfReport.review}
                  decisions={reviewDecisions}
                  setDecisions={setReviewDecisions}
                  drafts={reviewDrafts}
                  setDrafts={setReviewDrafts}
                  collisionChoices={reviewCollisions}
                  setCollisionChoices={setReviewCollisions}
                  conflictChoices={reviewConflicts}
                  setConflictChoices={setReviewConflicts}
                />
              )}

              {/* P0-6 FASE 2G · PRE-APPLY finale (read-only) */}
              {isSuper && vcfReport && (
                <div
                  className="rounded-lg border-2 border-purple-300 bg-purple-50 p-4 space-y-3"
                  data-testid="waba-vcf-preapply-section"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-lg text-purple-900">
                        PRE-APPLY FINALE CRM
                      </h3>
                      <p className="text-xs text-purple-800">
                        Simulazione READ-ONLY di cosa produrrà il futuro
                        APPLY combinando VCF + decisioni admin.
                        Nessuna scrittura CRM/WABA/Meta.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={runPreApply}
                      disabled={preApplyLoading}
                      className="px-4 py-2 rounded-full text-sm font-semibold bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-50"
                      data-testid="waba-vcf-preapply-run-btn"
                    >
                      {preApplyLoading ? "Elaborazione…"
                        : preApplyReport ? "Rigenera PRE-APPLY"
                        : "Genera PRE-APPLY"}
                    </button>
                  </div>
                  {preApplyErr && (
                    <div
                      className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
                      data-testid="waba-vcf-preapply-error"
                    >
                      {preApplyErr}
                    </div>
                  )}
                  {preApplyReport && (
                    <div
                      className="space-y-3"
                      data-testid="waba-vcf-preapply-report"
                    >
                      <div className={
                        "rounded-md px-3 py-2 text-sm font-semibold " +
                        (preApplyReport.status === "READY_FOR_APPLY"
                          ? "bg-emerald-100 text-emerald-900 border border-emerald-300"
                          : "bg-red-100 text-red-900 border border-red-300")
                      }
                      data-testid={`waba-vcf-preapply-status-${preApplyReport.status}`}
                      >
                        Stato: {preApplyReport.status === "READY_FOR_APPLY"
                          ? "✅ PRONTO PER APPLY"
                          : "🔴 BLOCCATO"}
                      </div>
                      {preApplyReport.blocking?.length > 0 && (
                        <ul className="text-xs text-red-900 list-disc pl-5 space-y-1">
                          {preApplyReport.blocking.map((b, i) => (
                            <li key={i}
                                data-testid={`waba-vcf-preapply-block-${i}`}>{b}</li>
                          ))}
                        </ul>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                        {[
                          ["Contatti VCF totali", "vcf_contacts_total"],
                          ["Telefoni unici validi", "phones_unique_valid"],
                          ["CRM attuale", "crm_current_count"],
                          ["Nuovi da creare", "new_to_create"],
                          ["Da collegare (esistenti)", "existing_to_link"],
                          ["Da aggiornare", "existing_to_update"],
                          ["Ignorati (Non importare)", "skipped_no_import"],
                          ["Duplicati VCF unificati", "vcf_duplicates_merged"],
                          ["Conflitti · mantieni CRM", "conflicts_kept_crm"],
                          ["Conflitti · usa VCF", "conflicts_used_vcf"],
                          ["Con note da importare", "records_with_notes"],
                          ["Solo Cognome (no Nome)", "only_surname_no_name"],
                          ["Senza cognome", "no_surname"],
                          ["Errori bloccanti", "errors_blocking"],
                          ["CRM previsto dopo APPLY", "crm_customers_after_expected"],
                        ].map(([label, key]) => (
                          <div key={key}
                              className="bg-white rounded p-2 border border-purple-200"
                              data-testid={`waba-vcf-preapply-count-${key}`}>
                            <div className="text-purple-700">{label}</div>
                            <div className="font-mono font-bold text-purple-900 text-sm">
                              {preApplyReport.counts?.[key] ?? "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                      <details className="text-xs text-purple-800">
                        <summary className="cursor-pointer font-semibold">
                          Diagnostica review
                        </summary>
                        <pre className="mt-2 bg-white p-2 rounded border border-purple-200 overflow-auto">
{JSON.stringify(preApplyReport.review, null, 2)}
                        </pre>
                      </details>

                      {/* APPLY reale · disabilitato se non READY */}
                      <div className="mt-4 pt-3 border-t border-purple-300 space-y-2">
                        <h4 className="font-semibold text-purple-900 text-sm">
                          APPLY REALE VCF → CRM
                        </h4>
                        <p className="text-xs text-purple-800">
                          Esecuzione idempotente. Scrive su `customers`.
                          Nessuna modifica a prenotazioni/WhatsApp/WABA/Meta.
                        </p>
                        {vcfApplyErr && (
                          <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
                                data-testid="waba-vcf-apply-error">
                            {vcfApplyErr}
                          </div>
                        )}
                        {vcfApplyResult && (
                          <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
                                data-testid="waba-vcf-apply-result">
                            <strong>APPLY completato</strong> ·
                            {" "}created={vcfApplyResult.final_summary?.created} ·
                            {" "}linked={vcfApplyResult.final_summary?.linked} ·
                            {" "}updated={vcfApplyResult.final_summary?.updated} ·
                            {" "}skipped={vcfApplyResult.final_summary?.skipped_decision + (vcfApplyResult.final_summary?.skipped_no_phone||0) + (vcfApplyResult.final_summary?.skipped_already_imported||0)} ·
                            {" "}merged={vcfApplyResult.final_summary?.merged_duplicates} ·
                            {" "}notes={vcfApplyResult.final_summary?.notes_written} ·
                            {" "}errors={vcfApplyResult.final_summary?.errors}
                          </div>
                        )}
                        {!vcfApplyResult && (
                          <button
                            type="button"
                            disabled={preApplyReport.status !== "READY_FOR_APPLY" || vcfApplyLoading}
                            onClick={() => setShowApplyConfirm1(true)}
                            className="px-4 py-2 rounded-full text-sm font-semibold bg-red-700 text-white hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="waba-vcf-apply-btn"
                          >
                            {vcfApplyLoading ? "Applying…" : "Esegui APPLY reale"}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showApplyConfirm1 && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4"
                      role="dialog" data-testid="waba-vcf-apply-confirm1">
                  <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-5 space-y-3">
                    <h3 className="font-serif text-xl text-red-800">
                      ⚠️ Conferma APPLY reale · passaggio 1/2
                    </h3>
                    <p className="text-sm text-ink-800">
                      Stai per scrivere nel CRM produzione. L'operazione è
                      idempotente ma non può essere annullata automaticamente.
                    </p>
                    <ul className="text-xs text-ink-700 list-disc pl-4">
                      <li>Nuovi da creare: <strong>{preApplyReport?.counts?.new_to_create}</strong></li>
                      <li>Esistenti da collegare: <strong>{preApplyReport?.counts?.existing_to_link}</strong></li>
                      <li>Da aggiornare: <strong>{preApplyReport?.counts?.existing_to_update}</strong></li>
                      <li>CRM previsto dopo APPLY: <strong>{preApplyReport?.counts?.crm_customers_after_expected}</strong></li>
                    </ul>
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => setShowApplyConfirm1(false)}
                              className="px-3 py-2 rounded-full text-sm bg-white border border-ink-300 text-ink-700 hover:bg-ink-50"
                              data-testid="waba-vcf-apply-confirm1-cancel">
                        Annulla
                      </button>
                      <button type="button"
                              onClick={() => { setShowApplyConfirm1(false); setShowApplyConfirm2(true); }}
                              className="px-3 py-2 rounded-full text-sm font-semibold bg-red-700 text-white hover:bg-red-800"
                              data-testid="waba-vcf-apply-confirm1-next">
                        Prosegui a passaggio 2
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {showApplyConfirm2 && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/70 p-4"
                      role="dialog" data-testid="waba-vcf-apply-confirm2">
                  <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-5 space-y-3">
                    <h3 className="font-serif text-xl text-red-800">
                      Conferma APPLY reale · passaggio 2/2
                    </h3>
                    <p className="text-sm text-ink-800">
                      Digita <code className="bg-ink-100 px-1 rounded font-mono">APPLY-{vcfFingerprint?.slice(0,12)}</code> per confermare.
                    </p>
                    <input type="text" value={applyConfirmText}
                            onChange={(e) => setApplyConfirmText(e.target.value)}
                            className="w-full border border-red-300 rounded px-3 py-2 font-mono text-sm"
                            placeholder={`APPLY-${vcfFingerprint?.slice(0,12)}`}
                            data-testid="waba-vcf-apply-confirm2-input" />
                    <div className="flex justify-end gap-2">
                      <button type="button"
                              onClick={() => { setShowApplyConfirm2(false); setApplyConfirmText(""); }}
                              className="px-3 py-2 rounded-full text-sm bg-white border border-ink-300 text-ink-700 hover:bg-ink-50"
                              data-testid="waba-vcf-apply-confirm2-cancel">
                        Annulla
                      </button>
                      <button type="button" onClick={runVcfApply}
                              disabled={applyConfirmText !== `APPLY-${vcfFingerprint?.slice(0,12)}` || vcfApplyLoading}
                              className="px-3 py-2 rounded-full text-sm font-semibold bg-red-700 text-white hover:bg-red-800 disabled:opacity-40"
                              data-testid="waba-vcf-apply-confirm2-run">
                        Esegui APPLY definitivo
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {err && (
        <div
          className="mt-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
          data-testid="waba-bootstrap-error"
          role="alert"
        >
          {err}
        </div>
      )}

      {loading && !report && (
        <div
          className="mt-4 text-sm text-ink-500"
          data-testid="waba-bootstrap-loading"
        >
          Analisi in corso…
        </div>
      )}

      {report && (
        <div
          className="mt-5 space-y-5"
          data-testid="waba-bootstrap-report"
        >
          {/* Durante sessione historical → risultati NON definitivi */}
          {histActive && (
            <div
              className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-900"
              data-testid="waba-bootstrap-not-final-hint"
            >
              Risultati non definitivi · Una sessione di sincronizzazione
              storica è attiva. I contatori possono variare fino al
              termine della sessione. L'importazione è temporaneamente
              disabilitata.
            </div>
          )}

          {/* SAFE_TO_APPLY badge */}
          <div className="flex flex-wrap items-center gap-3">
            {report.safe_to_apply ? (
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
                data-testid="waba-bootstrap-safe-badge"
              >
                Analisi superata · Importazione sicura
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-200"
                data-testid="waba-bootstrap-unsafe-badge"
              >
                ATTENZIONE · Importazione non sicura
              </span>
            )}
            <span
              className="text-xs text-ink-500 font-mono"
              data-testid="waba-bootstrap-generated-at"
            >
              generato: {report.generated_at}
            </span>
            {typeof report.flag_state
              ?.WA_STATE_SYNC_CRM_UPSERT_ENABLED !== "undefined" && (
              <span
                className={
                  "text-xs px-2 py-0.5 rounded-full border " +
                  (report.flag_state.WA_STATE_SYNC_CRM_UPSERT_ENABLED
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-ink-50 text-ink-600 border-ink-200")
                }
                data-testid="waba-bootstrap-flag-state"
              >
                Flag CRM sync:{" "}
                {report.flag_state.WA_STATE_SYNC_CRM_UPSERT_ENABLED
                  ? "ON"
                  : "OFF"}
              </span>
            )}
          </div>

          {/* RUBRICA + SIMULAZIONE side-by-side */}
          <div className="grid md:grid-cols-2 gap-4">
            <div
              className="rounded-lg border border-ink-100 bg-white overflow-hidden"
              data-testid="waba-bootstrap-directory-block"
            >
              <div className="bg-ink-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-700">
                Rubrica WhatsApp
              </div>
              <div>
                {DIRECTORY_LABELS.map(([key, label]) => (
                  <StatRow
                    key={key}
                    label={label}
                    value={report.directory?.[key]}
                    testId={`waba-bootstrap-dir-${key}`}
                  />
                ))}
              </div>
            </div>

            <div
              className="rounded-lg border border-ink-100 bg-white overflow-hidden"
              data-testid="waba-bootstrap-simulation-block"
            >
              <div className="bg-ink-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-700">
                Simulazione importazione
              </div>
              <div>
                {CLASSIFICATION_LABELS.map(([key, label]) => (
                  <StatRow
                    key={key}
                    label={label}
                    value={report.dry_run?.[key]}
                    danger={key === "COLLISION" || key === "ERROR"}
                    testId={`waba-bootstrap-sim-${key}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* RISULTATO FINALE */}
          <div
            className="rounded-lg border border-ink-100 bg-white overflow-hidden"
            data-testid="waba-bootstrap-result-block"
          >
            <div className="bg-ink-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-ink-700">
              Risultato finale
            </div>
            <div>
              <StatRow
                label="Clienti CRM attuali"
                value={report.customers_before}
                testId="waba-bootstrap-customers-before"
              />
              <StatRow
                label="Clienti CRM previsti dopo importazione"
                value={report.customers_after_expected}
                testId="waba-bootstrap-customers-after"
              />
              <StatRow
                label="Nuovi clienti netti previsti"
                value={report.net_new_customers_expected}
                testId="waba-bootstrap-net-new"
              />
              <StatRow
                label="Duplicati previsti"
                value={report.duplicates_expected}
                danger
                testId="waba-bootstrap-duplicates"
              />
            </div>
          </div>

          {/* COLLISIONI (PII-safe) */}
          {Array.isArray(report.collisions)
            && report.collisions.length > 0 && (
            <div
              className="rounded-lg border border-red-200 bg-red-50 overflow-hidden"
              data-testid="waba-bootstrap-collisions-block"
            >
              <div className="bg-red-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-red-800">
                Collisioni ({report.collisions.length})
              </div>
              <div className="p-3">
                <p className="text-xs text-red-700 mb-2">
                  Più contatti attivi risolvono allo stesso numero E.164.
                  Analizzare prima di procedere con l'importazione (APPLY
                  non disponibile in questa vista).
                </p>
                <div className="overflow-x-auto">
                  <table
                    className="min-w-full text-xs"
                    data-testid="waba-bootstrap-collisions-table"
                  >
                    <thead>
                      <tr className="text-left text-red-800">
                        <th className="px-2 py-1">Telefono (mascherato)</th>
                        <th className="px-2 py-1">Hash</th>
                        <th className="px-2 py-1 text-right">
                          Contatti coinvolti
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.collisions.map((c, i) => (
                        <tr
                          key={c.phone_hash8 || i}
                          data-testid={`waba-bootstrap-collision-row-${i}`}
                          className="border-t border-red-200/60"
                        >
                          <td
                            className="px-2 py-1 font-mono text-red-900"
                            data-testid={`waba-bootstrap-collision-${i}-suffix`}
                          >
                            {c.phone_suffix_masked}
                          </td>
                          <td
                            className="px-2 py-1 font-mono text-red-700"
                            data-testid={`waba-bootstrap-collision-${i}-hash`}
                          >
                            {c.phone_hash8}
                          </td>
                          <td
                            className="px-2 py-1 text-right font-semibold"
                            data-testid={`waba-bootstrap-collision-${i}-count`}
                          >
                            {c.smb_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* APPLY · CTA visibile SOLO se safe_to_apply=true E historical inactive */}
          {report.safe_to_apply && !applyResult && !applyBlocked && (
            <div
              className="flex flex-wrap items-center gap-3 border-t border-ink-100 pt-4"
              data-testid="waba-bootstrap-apply-cta-block"
            >
              <button
                type="button"
                onClick={() => setShowConfirm(true)}
                disabled={applying}
                className={
                  "px-4 py-2 rounded-full font-medium text-sm transition-colors " +
                  (applying
                    ? "bg-ink-200 text-ink-500 cursor-not-allowed"
                    : "bg-emerald-700 text-white hover:bg-emerald-800")
                }
                data-testid="waba-bootstrap-apply-btn"
              >
                Importa nel CRM
              </button>
              <span className="text-xs text-ink-500">
                L'operazione non elimina clienti, prenotazioni,
                conversazioni o storico.
              </span>
            </div>
          )}

          {/* APPLY · report post-esecuzione */}
          {applyErr && (
            <div
              className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
              data-testid="waba-bootstrap-apply-error"
              role="alert"
            >
              ATTENZIONE · {applyErr}
            </div>
          )}
          {applyResult && (
            <div
              className={
                "rounded-lg border overflow-hidden "
                + (applyResult.success && !applyResult.aborted
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-red-200 bg-red-50")
              }
              data-testid="waba-bootstrap-apply-result-block"
            >
              <div
                className={
                  "px-3 py-2 text-xs font-semibold uppercase tracking-wide "
                  + (applyResult.success && !applyResult.aborted
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-red-100 text-red-800")
                }
              >
                {applyResult.aborted
                  ? "ATTENZIONE · Importazione annullata"
                  : applyResult.success
                    ? "Importazione completata"
                    : "ATTENZIONE · Errori durante l'importazione"}
              </div>
              <div className="p-3">
                {applyResult.aborted ? (
                  <p
                    className="text-sm text-red-800"
                    data-testid="waba-bootstrap-apply-aborted-reason"
                  >
                    Motivo:{" "}
                    <code className="font-mono">
                      {applyResult.reason || "unsafe"}
                    </code>
                  </p>
                ) : (
                  <div>
                    {[
                      ["linked", "Collegati"],
                      ["created", "Creati"],
                      ["updated", "Aggiornati"],
                      ["noop_idempotent", "Invariati"],
                      ["skipped_inactive", "Ignorati (inattivi)"],
                      ["skipped_no_phone",
                          "Ignorati (senza telefono)"],
                      ["skipped_unnormalizable",
                          "Ignorati (telefono non valido)"],
                      ["collision_skipped", "Collisioni saltate"],
                      ["error", "Errori"],
                      ["duplicates_created",
                          "Duplicati creati"],
                    ].map(([key, label]) => (
                      <StatRow
                        key={key}
                        label={label}
                        value={applyResult.counts?.[key]}
                        danger={
                          key === "error"
                          || key === "duplicates_created"
                          || key === "collision_skipped"
                        }
                        testId={`waba-bootstrap-apply-${key}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* HIST START · modale esplicito con badge MOCK/LIVE */}
      {showHistConfirm && hist && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
          data-testid="waba-hist-confirm-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5">
            <h3 className="font-serif text-xl text-ink-900 mb-2">
              Avvia sincronizzazione rubrica
            </h3>
            <p className="text-sm text-ink-700 mb-3">
              WhatsApp Business ritrasmetterà l'intera rubrica del
              cellulare aziendale. Durante il processo le scritture live
              sul CRM saranno sospese.
            </p>
            <div className="mb-3">
              <span
                className={
                  "text-xs px-2 py-1 rounded-full border font-medium " +
                  (hist.meta_mock
                    ? "bg-amber-50 text-amber-800 border-amber-300"
                    : "bg-rose-50 text-rose-800 border-rose-300")
                }
                data-testid="waba-hist-modal-mode"
              >
                Modalità Meta: {hist.meta_mock ? "MOCK" : "LIVE"}
              </span>
            </div>
            {!hist.meta_mock && (
              <div
                className="rounded-md bg-rose-50 border border-rose-300 px-3 py-2 text-sm text-rose-900 mb-3"
                data-testid="waba-hist-modal-live-warning"
              >
                Questa operazione invierà una richiesta reale a WhatsApp
                Business.
              </div>
            )}
            {hist.meta_mock && (
              <div
                className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900 mb-3"
                data-testid="waba-hist-modal-mock-hint"
              >
                Nessuna chiamata reale sarà inviata a Meta. La sessione
                verrà comunque tracciata sul database per verifica.
              </div>
            )}
            {histActing && (
              <div
                className="text-sm text-ink-600 mb-3"
                data-testid="waba-hist-acting"
              >
                Avvio in corso…
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowHistConfirm(false)}
                disabled={histActing}
                className={
                  "px-3 py-2 rounded-full text-sm font-medium border border-ink-300 " +
                  (histActing
                    ? "text-ink-400 cursor-not-allowed"
                    : "text-ink-700 hover:bg-ink-50")
                }
                data-testid="waba-hist-cancel-btn"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={startHistSync}
                disabled={histActing}
                className={
                  "px-3 py-2 rounded-full text-sm font-semibold text-white " +
                  (histActing
                    ? "bg-indigo-400 cursor-not-allowed"
                    : "bg-indigo-700 hover:bg-indigo-800")
                }
                data-testid="waba-hist-confirm-btn"
              >
                {histActing ? "Avvio…" : "Conferma avvio"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFERMA IMPORTAZIONE · modale esplicito */}
      {showConfirm && report && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4"
          data-testid="waba-bootstrap-confirm-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-5">
            <h3 className="font-serif text-xl text-ink-900 mb-2">
              Conferma importazione
            </h3>
            <p className="text-sm text-ink-700 mb-3">
              Stai per importare la rubrica WhatsApp nel CRM.
            </p>
            <ul className="text-sm text-ink-800 space-y-1 mb-3 font-mono">
              <li data-testid="waba-bootstrap-confirm-link-count">
                Clienti esistenti da collegare:{" "}
                <strong>
                  {report.dry_run?.WOULD_LINK_EXISTING ?? 0}
                </strong>
              </li>
              <li data-testid="waba-bootstrap-confirm-create-count">
                Nuovi clienti da creare:{" "}
                <strong>{report.dry_run?.WOULD_CREATE ?? 0}</strong>
              </li>
              <li data-testid="waba-bootstrap-confirm-update-count">
                Clienti da aggiornare:{" "}
                <strong>
                  {report.dry_run?.WOULD_UPDATE_NAME ?? 0}
                </strong>
              </li>
            </ul>
            <p className="text-xs text-ink-500 mb-4">
              L'operazione non elimina clienti, prenotazioni,
              conversazioni o storico.
            </p>
            {applying && (
              <div
                className="text-sm text-ink-600 mb-3"
                data-testid="waba-bootstrap-applying-status"
              >
                Importazione in corso…
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={applying}
                className={
                  "px-3 py-2 rounded-full text-sm font-medium border border-ink-300 " +
                  (applying
                    ? "text-ink-400 cursor-not-allowed"
                    : "text-ink-700 hover:bg-ink-50")
                }
                data-testid="waba-bootstrap-cancel-btn"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={runApply}
                disabled={applying}
                className={
                  "px-3 py-2 rounded-full text-sm font-semibold text-white " +
                  (applying
                    ? "bg-emerald-400 cursor-not-allowed"
                    : "bg-emerald-700 hover:bg-emerald-800")
                }
                data-testid="waba-bootstrap-confirm-btn"
              >
                {applying ? "Importazione…" : "Conferma importazione"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default WabaDirectoryBootstrapPanel;
